import React, { useState, useEffect } from 'react';
import { Crown, Gift, MessageCircle, Zap, Clock, ArrowRight, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import apiClient from '@/utils/api';

const SellerSubscriptionCards = ({ token }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    apiClient.get('/marketplace/my-subscription', {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(r => setSubscription(r.data))
    .catch(() => {})
    .finally(() => setLoading(false));
  }, [token]);

  if (loading || !subscription) return null;

  const { plan_name, plan_config, quota, addons, is_paid } = subscription;
  const usedPercent = quota.max_active_listings > 0 
    ? Math.round((quota.active_listings / quota.max_active_listings) * 100) 
    : 0;

  return (
    <div className="space-y-4" data-testid="seller-subscription-cards">
      {/* Current Plan Card */}
      <div className={`rounded-xl border-2 p-5 ${is_paid ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-white'}`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {is_paid ? (
              <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                <Crown className="w-5 h-5 text-white" />
              </div>
            ) : (
              <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                <Gift className="w-5 h-5 text-slate-600" />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-base" data-testid="current-plan-name">
                {is_paid ? 'Seller Basic' : 'Free Plan'}
              </h3>
              <p className="text-xs text-slate-500">
                {plan_config?.description || (is_paid ? 'Active membership' : 'Free listing plan')}
              </p>
            </div>
          </div>
          {!is_paid && (
            <Button size="sm" className="rounded-lg bg-emerald-500 hover:bg-emerald-600 text-xs h-8"
              onClick={() => window.location.href = '/sell/individual#pricing'}
              data-testid="upgrade-plan-btn"
            >
              <Crown className="w-3.5 h-3.5 mr-1" /> Upgrade
            </Button>
          )}
        </div>

        {/* Quota Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Active Listings</span>
            <span className="font-medium" data-testid="listing-quota-text">
              {quota.active_listings}/{quota.max_active_listings}
            </span>
          </div>
          <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all ${usedPercent >= 100 ? 'bg-red-500' : usedPercent >= 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${Math.min(100, usedPercent)}%` }}
              data-testid="listing-quota-bar"
            />
          </div>
          <div className="flex justify-between text-xs text-slate-500">
            <span>{quota.listing_validity_days}-day validity per listing</span>
            <span>{quota.remaining_listings} remaining</span>
          </div>
        </div>
      </div>

      {/* Quick Actions Row */}
      <div className="grid grid-cols-2 gap-3">
        {/* WhatsApp Alerts Card */}
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="whatsapp-addon-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <MessageCircle className="w-4 h-4 text-green-600" />
            </div>
            <span className="text-sm font-medium">WhatsApp Alerts</span>
          </div>
          {addons && addons.length > 0 ? (
            <div className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle className="w-3.5 h-3.5" /> Active
            </div>
          ) : (
            <Button size="sm" variant="outline" className="w-full h-7 text-xs rounded-lg border-green-300 text-green-700 hover:bg-green-50 mt-1"
              onClick={() => window.location.href = '/sell/individual#addons'}
            >
              From ₹290
            </Button>
          )}
        </div>

        {/* Boost Card */}
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="boost-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
              <Zap className="w-4 h-4 text-amber-600" />
            </div>
            <span className="text-sm font-medium">Boost Listing</span>
          </div>
          <Button size="sm" variant="outline" className="w-full h-7 text-xs rounded-lg border-amber-300 text-amber-700 hover:bg-amber-50 mt-1"
            onClick={() => window.location.href = '/sell/individual#pricing'}
          >
            From ₹499
          </Button>
        </div>
      </div>

      {/* Extension reminder for free users */}
      {!is_paid && quota.active_listings > 0 && (
        <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 flex items-start gap-3" data-testid="extend-reminder">
          <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-900">Listing expires in {quota.listing_validity_days} days</p>
            <p className="text-xs text-blue-700 mt-1">Extend for ₹199 (30 more days) or upgrade plan for unlimited extensions.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerSubscriptionCards;
